import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import CalendarWidget from 'platform/shared/calendar/CalendarWidget';

import Wrapper from '../layout/Wrapper';
import {
  setSelectedSlot,
  selectSelectedSlot,
  selectUuid,
} from '../redux/slices/formSlice';
import { useGetAppointmentAvailabilityQuery } from '../redux/api/vassApi';
import { useErrorFocus } from '../hooks/useErrorFocus';
import { mapAppointmentAvailabilityToSlots } from '../utils/slots';
import {
  getTimezoneDescByTimeZoneString,
  getBrowserTimezone,
} from '../utils/timezone';
import {
  isAppointmentNotFoundError,
  isNoSlotsAvailableError,
  isNotWhithinCohortError,
  isServerError,
} from '../utils/errors';
import { removeVassToken } from '../utils/auth';
import { URLS } from '../utils/constants';
import { pathWithSearch } from '../utils/invitationQuery';

const DateTimeSelection = () => {
  const dispatch = useDispatch();
  const selectedSlot = useSelector(selectSelectedSlot);
  const uuid = useSelector(selectUuid);
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    data: appointmentAvailability,
    isLoading: loading,
    error: appointmentAvailabilityError,
  } = useGetAppointmentAvailabilityQuery(uuid);
  const [{ error, handleSetError }] = useErrorFocus([
    '.vaos-calendar__validation-msg',
  ]);
  const isNavigatingAway = useRef(false);

  const timezone = getBrowserTimezone();

  const slots = mapAppointmentAvailabilityToSlots(appointmentAvailability);

  // Warn on back button
  useEffect(() => {
    const pathAndSearch = `${window.location.pathname}${window.location.search}`;
    window.history.pushState(null, '', pathAndSearch);

    const handlePopState = () => {
      if (isNavigatingAway.current) {
        return;
      }

      // eslint-disable-next-line no-alert
      const confirmLeave = window.confirm(
        'This page is asking you to confirm that you want to leave — information you’ve entered may not be saved.',
      );

      if (!confirmLeave) {
        window.history.pushState(
          null,
          '',
          `${window.location.pathname}${window.location.search}`,
        );
      } else {
        isNavigatingAway.current = true;
        removeVassToken();
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // This is for loading not sure if we will need it
  const disabledMessage = null;

  const latestAvailableSlot = new Date(slots[slots.length - 1]?.end);

  const onChange = selectedDateTimes => {
    // Selecting a day on the calendar fires an onChange event with an empty array even
    // when the day is is the same as the currently selected slot time. To avoid
    // deselecting the slot, we don't save anything if the selected date times is an empty array
    const selectedSlotTime = selectedDateTimes[0];
    if (!selectedSlotTime) {
      return;
    }
    const selectedSlotData = slots.find(
      slot => slot.start === selectedSlotTime,
    );
    if (!selectedSlotData) {
      dispatch(
        setSelectedSlot({
          dtStartUtc: null,
          dtEndUtc: null,
        }),
      );
      return;
    }
    dispatch(
      setSelectedSlot({
        dtStartUtc: selectedSlotData.start,
        dtEndUtc: selectedSlotData.end,
      }),
    );
    handleSetError('');
  };

  const handleContinue = () => {
    if (!selectedSlot.dtStartUtc || !selectedSlot.dtEndUtc) {
      handleSetError('Choose a preferred date and time for your appointment');
      return;
    }
    navigate(pathWithSearch(URLS.TOPIC_SELECTION, search));
  };

  return (
    <Wrapper
      pageTitle="When do you want to schedule your call?"
      classNames="vads-u-margin-top--4"
      testID="date-time-selection"
      required
      loading={loading}
      loadingMessage="Loading appointment availability. This may take up to 30 seconds. Please don’t refresh the page."
      errorAlert={
        isServerError(appointmentAvailabilityError) ||
        isNotWhithinCohortError(appointmentAvailabilityError) ||
        isNoSlotsAvailableError(appointmentAvailabilityError) ||
        isAppointmentNotFoundError(appointmentAvailabilityError)
      }
    >
      <div data-testid="content">
        <p>
          Select an available date and time from the calendar below. Phone
          appointment times are displayed in{' '}
          {getTimezoneDescByTimeZoneString(timezone)}.
        </p>
        <p>
          <strong>Note:</strong> You can schedule a call on a weekday within the
          next 2 weeks.
        </p>
      </div>

      <CalendarWidget
        maxSelections={1}
        availableSlots={slots}
        value={selectedSlot?.dtStartUtc ? [selectedSlot.dtStartUtc] : []}
        id="dateTime"
        data-testid="dateTime"
        timezone={timezone}
        additionalOptions={{
          required: true,
        }}
        disabledMessage={disabledMessage}
        onChange={onChange}
        onNextMonth={null}
        onPreviousMonth={null}
        minDate={new Date()}
        maxDate={latestAvailableSlot}
        required
        requiredMessage={error}
        startMonth={new Date()}
        showValidation={!!error}
        showWeekends
        overrideMaxDays
      />
      <va-button
        data-testid="continue-button"
        continue
        onClick={handleContinue}
        text={null}
      />
    </Wrapper>
  );
};

export default DateTimeSelection;
